import {
  createContext,
  useContext,
  useSyncExternalStore,
  type Context,
  type ReactNode,
} from "react";
import type { ComposerState, ComposerStore } from "./composer-store";
import type { DispatchResult, EditorIntent } from "./handle";

/**
 * Your own content in the composer's chrome. Each is rendered inside the editor, so a component in
 * one can call `useComposerState()` and `useComposerDispatch()`. None is rendered while the
 * document is loading or could not be read: there is no editor for it to talk to yet.
 */
export interface ComposerSlots {
  /** Attached above the frame: a notice, or what went wrong with the last send. */
  header?: ReactNode;
  /** At the inline end of the formatting toolbar, and rendered only while that shows. */
  toolbarEnd?: ReactNode;
  /** The start of the row under the text: attach, emoji, mention. */
  footerStart?: ReactNode;
  /** The end of that row, before the send control. */
  footerEnd?: ReactNode;
  /**
   * Your send control in place of the built-in one, shown when that would be: `sendButton`,
   * `onSubmit`, and `readOnly` decide. A function called while rendering, not a component, so it
   * cannot call hooks; return a component of yours if you need them.
   */
  send?: (props: ComposerSendProps) => ReactNode;
}

export interface ComposerSendProps {
  /** Sends the draft, as Enter does. Nothing happens for an empty draft or during a send. */
  submit: () => void;
  pending: boolean;
  empty: boolean;
}

export interface ComposerContextValue {
  store: ComposerStore;
  dispatch: (intent: EditorIntent) => DispatchResult;
}

export const ComposerContext: Context<ComposerContextValue | null> =
  createContext<ComposerContextValue | null>(null);

function useComposer(hook: string): ComposerContextValue {
  const value = useContext(ComposerContext);
  // An inert answer would say "empty" forever, and a send control built on it would never light up.
  if (value === null) {
    throw new Error(`${hook}() works only in the slots of <Editor preset="composer">.`);
  }
  return value;
}

/**
 * What the composer's controls show: the marks, link, and block at the selection, and whether the
 * draft is empty or being sent. It changes when one of those does, never on typing alone.
 */
export function useComposerState(): ComposerState {
  const { store } = useComposer("useComposerState");
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/** One field of it, for a control that should re-render for nothing else. */
export function useComposerValue<Key extends keyof ComposerState>(key: Key): ComposerState[Key] {
  const { store } = useComposer("useComposerValue");
  const get = (): ComposerState[Key] => store.getSnapshot()[key];
  return useSyncExternalStore(store.subscribe, get, get);
}

/** The handle's `dispatch`, for a control in a slot: no ref to pass down. */
export function useComposerDispatch(): (intent: EditorIntent) => DispatchResult {
  return useComposer("useComposerDispatch").dispatch;
}
